import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { DownloadDocumentIcon } from "../Icons/Icons";
import { DropDownButton } from "../DropDownButton/DropDownButton";
import {
  exportCsv,
  exportXlsx,
  exportCsvRows,
  exportXlsxRows,
} from "./tableExport";
import type { ExportCell } from "./tableExport";
import type { StorefrontChatbotConfiguration } from "../../types/types";

const Container = styled.div`
  width: 100%;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0.25em 0 0.75em;
`;

const ExportButton = styled(DropDownButton)`
  button {
    min-height: 28px;
    padding: ${({ theme }) => theme.resolve("spacing_xs")}
      ${({ theme }) => theme.resolve("spacing_md")};
    border: none;
    font-size: ${({ theme }) => theme.fontSizes.small};
    line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  }

  button:hover,
  button:focus,
  button:active {
    border: none;
  }

  button span[class*="DropDownButton__ButtonContent"] {
    justify-content: center;
    gap: ${({ theme }) => theme.resolve("spacing_sm")};
  }

  button span[class*="DropDownButton__ButtonContent"] > span {
    margin-right: 0 !important;
  }

  button span[class*="DropDownButton__ButtonContent"] > svg {
    width: 14px;
    height: 14px;
    display: block;
    flex-shrink: 0;
  }

  button span[class*="DropDownButton__ButtonContent"] > span:first-child svg {
    width: 18px !important;
    height: 18px !important;
    display: block;
  }

  button span[class*="DropDownButton__ButtonContent"] > span:first-child {
    height: 18px !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  font-size: 0.95em;
  & th,
  & td {
    border: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.secondaryBorder};
    min-width: 96px;
    overflow-wrap: normal;
    word-break: normal;
    padding: ${({ theme }) => theme.resolve("spacing_md")}
      ${({ theme }) => theme.resolve("spacing_lg")};
    text-align: left;
    vertical-align: top;
  }
  & th {
    background-color: #f2f4f6;
    font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  }
  & th:first-child,
  & td:first-child {
    min-width: 120px;
  }
  & tbody tr:nth-child(even) {
    background: #f8f9fa;
  }
`;

export function ExportableTable({
  children,
  loadRows,
  totalRows,
  chatBotName,
  chatBotConfig,
  ...rest
}: React.TableHTMLAttributes<HTMLTableElement> & {
  loadRows?: () => Promise<ExportCell[][]>;
  totalRows?: number;
  chatBotName?: string;
  chatBotConfig?: StorefrontChatbotConfiguration;
}) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);
  const exportingRef = useRef(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const exportLabels = [t("CSV"), t("Excel")] as const;
  const exportName = chatBotName || chatBotConfig?.name || "agilis";

  const onCsv = () => {
    if (tableRef.current) {
      if (chatBotConfig) {
        exportCsv({ table: tableRef.current, chatBotName: exportName });
      } else {
        exportCsv(tableRef.current, exportName);
      }
    }
  };

  const onXlsx = () => {
    if (tableRef.current) {
      if (chatBotConfig) {
        void exportXlsx({ table: tableRef.current, chatBotName: exportName });
      } else {
        void exportXlsx(tableRef.current, exportName);
      }
    }
  };

  const handleExportSelection = (item: string) => {
    if (loadRows) {
      if (exportingRef.current) return;
      exportingRef.current = true;
      setExporting(true);
      setExportError(false);
      void loadRows()
        .then((rows) =>
          item === exportLabels[0]
            ? exportCsvRows(rows, exportName)
            : exportXlsxRows(rows, exportName)
        )
        .catch(() => setExportError(true))
        .finally(() => {
          exportingRef.current = false;
          setExporting(false);
        });
      return;
    }
    if (item === exportLabels[0]) {
      onCsv();
      return;
    }

    if (item === exportLabels[1]) {
      onXlsx();
      return;
    }
  };

  return (
    <Container aria-busy={exporting}>
      <TableScroll>
        <StyledTable ref={tableRef} {...rest}>
          {children}
        </StyledTable>
      </TableScroll>
      <Toolbar>
        <ExportButton
          items={[...exportLabels]}
          textLeft={
            exporting
              ? t("Preparing export…")
              : loadRows
              ? t("Export all {{count}}", { count: totalRows })
              : t("Export displayed rows")
          }
          isDisabled={exporting}
          icon={<DownloadDocumentIcon width={18} height={18} />}
          clickHandler={handleExportSelection}
          variant="tertiary"
        />
      </Toolbar>
      {exportError && (
        <p role="alert">
          {t(
            "Unable to export this table. Please try again or run the search again."
          )}
        </p>
      )}
    </Container>
  );
}
